import {
  getProductColorModel,
  getProductModel,
} from 'services/gtm/gtmProductAttributes';
import { decimalRound, getHTPrice } from 'services/gtm/gtmUtils';
import { GtmInputFilterType, GtmInputProductType } from 'types/gtm/InputData';
import { GtmProductOutputType } from 'types/gtm/OutputData';

type Calculator<TInput, TOutput, TAdditional> = {
  [K in keyof TOutput]: (
    input: TInput,
    computed: Partial<TOutput>,
    additionalData: TAdditional
  ) => TOutput[K];
};

// @TODO - Add {attribute} instead of product
const PRODUCT_DATA_CALCULATOR: Calculator<
  GtmInputProductType,
  GtmProductOutputType,
  { finalPrice?: number; addToCartType?: string; access?: string }
> = {
  r: product => {
    const { realSku } = product;
    return realSku ? getProductModel(realSku) : undefined;
  },
  rc: product => {
    const { realSku } = product;
    return realSku ? getProductColorModel(realSku) : undefined;
  },
  rct: product => product.realSku,
  name: product => product.name?.toLowerCase(),
  brand: product => product.brandName?.toLowerCase() || 'afibel',
  variant: product => product.realSku,
  color: product => product.color,
  size: product => product.size,
  category_1: product => product.category1?.toLowerCase(),
  category_2: product => product.category2?.toLowerCase(),
  category_3: product => product.category3?.toLowerCase(),
  list_position: product => product.listPosition,
  price_tf_original: (product, computed, additionalData) => {
    const { discountPrice } = product;
    const price = discountPrice ? discountPrice : additionalData.finalPrice;
    return price ? getHTPrice(price) : undefined;
  },
  price_ati_original: (product, computed, additionalData) => {
    const { discountPrice } = product;
    return discountPrice ? discountPrice : additionalData.finalPrice;
  },
  price_tf_after_discount: (_1, _2, additionalData) => {
    const { finalPrice } = additionalData;
    return finalPrice ? getHTPrice(finalPrice) : undefined;
  },
  price_ati_after_discount: (_1, _2, additionalData) =>
    additionalData.finalPrice,
  discount_ati: (_, computed) => {
    const { price_ati_original, price_ati_after_discount } = computed;
    return price_ati_original && price_ati_after_discount
      ? decimalRound(price_ati_original - price_ati_after_discount)
      : undefined;
  },
  discount_tf: (_, computed) => {
    const { price_tf_original, price_tf_after_discount } = computed;
    return price_tf_original && price_tf_after_discount
      ? decimalRound(price_tf_original - price_tf_after_discount)
      : undefined;
  },
  quantity: product => product.quantity,
  coupon: () => undefined,
  add_to_cart_type: (_1, _2, additionalData) => additionalData.addToCartType,
  sticker: product => product.stickerOpeco?.split('¤')[0].toLowerCase(),
  access: (_1, _2, additionalData) => additionalData.access,
  stock: product => product.availabilityStatus?.toLocaleLowerCase(),
};

function computeAttributes<TInput, TOutput, TAdditional>(
  inputObject: TInput,
  keys: string[],
  calculators: Calculator<TInput, TOutput, TAdditional>,
  // @TODO - Pass specific type ?
  additionalData?: Record<string, unknown>
): Partial<TOutput> {
  const computed: Partial<TOutput> = {};

  for (const key of keys) {
    if (key in calculators) {
      computed[key] = calculators[key](inputObject, computed, additionalData);
    }
  }

  return computed;
}

export function getGtmAppliedFilterValues(
  filters: GtmInputFilterType[]
): string | undefined {
  return filters.length > 0
    ? filters.map(({ label }) => label).join('|')
    : undefined;
}

export function getGtmProductData(
  product: GtmInputProductType,
  outputAttributes: string[],
  additionalData?: { access?: string }
): GtmProductOutputType {
  return computeAttributes(product, outputAttributes, PRODUCT_DATA_CALCULATOR, {
    finalPrice: product.finalPrice,
    access: additionalData?.access,
  });
}

export function getGtmProductsData(
  products: GtmInputProductType[],
  outputAttributes: string[],
  additionalData?: { access?: string }
): GtmProductOutputType[] {
  return products.map(product =>
    getGtmProductData(product, outputAttributes, additionalData)
  );
}
